/** Edit a single auth_methods / client_auth_methods entry.
 *
 * Field set mirrors the tour-operator creation wizard's Step 6:
 *   - local:  just `name` (label)
 *   - auth0:  name, domain, client_id, allow_signup
 *             when allow_signup: allowed_domains + access_groups (ids)
 *
 * Any other `backend` value is treated like "local" but its backend label
 * is preserved on save (so we don't silently rewrite unknown auth methods).
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

/** Stored shape of one entry in `config.auth_methods` / `client_auth_methods`. */
export interface AuthMethod {
  backend: string;
  name?: string;
  domain?: string;
  client_id?: string;
  allow_signup?: boolean;
  allowed_domains?: string[];
  /** Resolved access group ids (numbers); the wizard sends codes, the
   *  backend resolves them at creation time. */
  access_groups?: number[];
  // Allow other backend-specific fields to pass through unchanged.
  [extra: string]: unknown;
}

interface Props {
  open: boolean;
  /** The method being edited; `null` if the dialog is closed. */
  method: AuthMethod | null;
  readOnly?: boolean;
  onSave: (next: AuthMethod) => void;
  onCancel: () => void;
}

export function AuthMethodEditDialog({ open, method, readOnly = false, onSave, onCancel }: Props) {
  const [draft, setDraft] = useState<AuthMethod | null>(method);
  const [domainInput, setDomainInput] = useState('');
  const [groupInput, setGroupInput] = useState('');

  // Reset draft state whenever a different method is opened for editing.
  useEffect(() => {
    setDraft(method);
    setDomainInput('');
    setGroupInput('');
  }, [method]);

  if (!draft) return null;

  function patch(partial: Partial<AuthMethod>) {
    setDraft((prev) => (prev ? { ...prev, ...partial } : prev));
  }

  function addAllowedDomain() {
    const v = domainInput.trim().toLowerCase();
    if (!v) return;
    const list = draft!.allowed_domains ?? [];
    if (!list.includes(v)) patch({ allowed_domains: [...list, v] });
    setDomainInput('');
  }

  function removeAllowedDomain(d: string) {
    patch({ allowed_domains: (draft!.allowed_domains ?? []).filter((x) => x !== d) });
  }

  function addAccessGroup() {
    const v = groupInput.trim();
    if (!v) return;
    const id = Number(v);
    if (!Number.isFinite(id)) return;
    const list = draft!.access_groups ?? [];
    if (!list.includes(id)) patch({ access_groups: [...list, id] });
    setGroupInput('');
  }

  function removeAccessGroup(id: number) {
    patch({ access_groups: (draft!.access_groups ?? []).filter((x) => x !== id) });
  }

  const isAuth0 = draft.backend === 'auth0';
  const isLocal = draft.backend === 'local';

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle>
        {isAuth0 ? 'Edit Auth0 method' : isLocal ? 'Edit local method' : `Edit ${draft.backend} method`}
      </DialogTitle>
      <DialogContent dividers>
        {!isAuth0 && !isLocal && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This auth method uses the <strong>{draft.backend}</strong> backend, which has no
            assisted editor in this view. Only the display name is editable here; use the
            Raw tab to change other properties.
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Name — always editable */}
          <Grid item xs={12} md={isAuth0 ? 6 : 12}>
            <TextField
              label="Display name"
              fullWidth
              size="small"
              value={draft.name ?? ''}
              onChange={(e) => patch({ name: e.target.value })}
              disabled={readOnly}
              autoFocus
            />
          </Grid>

          {/* Auth0-only fields */}
          {isAuth0 && (
            <>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Auth0 domain"
                  fullWidth
                  size="small"
                  placeholder="example.auth0.com"
                  value={draft.domain ?? ''}
                  onChange={(e) => patch({ domain: e.target.value })}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Client ID"
                  fullWidth
                  size="small"
                  value={draft.client_id ?? ''}
                  onChange={(e) => patch({ client_id: e.target.value })}
                  disabled={readOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(draft.allow_signup)}
                      onChange={(e) => patch({ allow_signup: e.target.checked })}
                      disabled={readOnly}
                    />
                  }
                  label="Allow new user sign-up via this provider"
                />
              </Grid>

              {draft.allow_signup && (
                <>
                  {/* Allowed domains */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Allowed email domains (staff users only — leave empty to block all auto-signup).
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
                      <TextField
                        label="Domain (e.g. example.com)"
                        size="small"
                        value={domainInput}
                        onChange={(e) => setDomainInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAllowedDomain(); } }}
                        sx={{ flex: 1 }}
                        disabled={readOnly}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(draft.allowed_domains ?? []).map((d) => (
                        <Chip
                          key={d}
                          label={d}
                          size="small"
                          onDelete={readOnly ? undefined : () => removeAllowedDomain(d)}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Access group IDs */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Access group IDs to assign to new users (numeric ids as stored in the
                      live config; resolve names via the dashboard if needed).
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5 }}>
                      <TextField
                        label="Group id (number)"
                        size="small"
                        type="number"
                        value={groupInput}
                        onChange={(e) => setGroupInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAccessGroup(); } }}
                        sx={{ flex: 1 }}
                        disabled={readOnly}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(draft.access_groups ?? []).map((id) => (
                        <Chip
                          key={id}
                          label={`#${id}`}
                          size="small"
                          onDelete={readOnly ? undefined : () => removeAccessGroup(id)}
                        />
                      ))}
                    </Box>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave(draft)}
          disabled={readOnly}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
